import type { TFunction } from 'i18next';
import type { ChangeEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BatchResult, ValidationResult } from '../../api/batch';
import { uploadBatchPhotos, validateBatch } from '../../api/batch';
import { downscaleToFit } from '../../utils/downscale';
import { extractZip } from '../../utils/unzip';
import type { BatchUploadFlowState } from './BatchUpload.flow';

const UPLOAD_BATCH_SIZE = 50;

export interface PendingFile {
  upn: string;
  filename: string;
  blob: Blob;
  previewUrl: string;
}

type Phase =
  'idle' | 'extracting' | 'validating' | 'ready' | 'uploading' | 'done';

type ReadOutcome = { files: PendingFile[] } | { error: string };

type ReadFile = Omit<PendingFile, 'previewUrl'>;

const PHOTO = /\.(jpg|jpeg|png)$/i;

/**
 * The photos at the root of the ZIP, as files, one entry at a time so
 * `readImages` never holds more than one full-resolution photo. Anything in a
 * folder is skipped, macOS's `__MACOSX/._name.jpg` resource forks included
 * (named like photos, they are not images). `\` for archivers that write
 * Windows separators.
 */
async function* zipPhotos(zip: File): AsyncGenerator<File> {
  for await (const { name, data } of extractZip(zip)) {
    if (/[/\\]/.test(name) || !PHOTO.test(name)) {
      continue;
    }
    yield new File([new Uint8Array(data)], name, {
      type: /\.png$/i.test(name) ? 'image/png' : 'image/jpeg',
    });
  }
}

async function readZip(
  zip: File,
  t: TFunction,
  onCount: (n: number) => void,
): Promise<ReadOutcome> {
  const files = await readImages(zipPhotos(zip), onCount);
  if (files.length === 0) {
    return { error: t('batchUpload.noJpeg') };
  }
  return { files };
}

/** The single `.zip` of the selection, if that is what it is. */
function selectedZip(files: File[]): File | undefined {
  const [only] = files;
  if (files.length === 1 && only && only.name.toLowerCase().endsWith('.zip')) {
    return only;
  }
  return undefined;
}

/** Why a photo selection is refused, or `null` when it is all JPEG or PNG. */
function refusedTypes(files: File[], t: TFunction): string | null {
  const invalid = files.filter(
    (f) => f.type !== 'image/jpeg' && f.type !== 'image/png',
  );
  if (invalid.length === 0) {
    return null;
  }
  return t('batchUpload.onlyJpeg', {
    list: invalid.map((f) => f.name).join(', '),
  });
}

async function readImages(
  files: Iterable<File> | AsyncIterable<File>,
  onCount: (n: number) => void,
): Promise<PendingFile[]> {
  // One at a time on purpose: Array.fromAsync awaits each photo before the
  // next, and each one decodes a full-resolution photo to a bitmap, so a
  // Promise.all over a big selection would hold all of them in memory at once.
  const extracted: ReadFile[] = await Array.fromAsync(files, async (f, i) => {
    const blob = await downscaleToFit(f);
    // Per-file setState re-rendered the whole tree once per file; every 25
    // files keeps the progress feel without the churn.
    if ((i + 1) % 25 === 0) {
      onCount(i + 1);
    }
    return { filename: f.name, upn: f.name.replace(PHOTO, ''), blob };
  });
  onCount(extracted.length);
  // Preview URLs are created only once every file has been read: a photo that
  // fails half-way through then leaves no URL behind, where creating them as
  // it went leaked every one already made (nothing would revoke them).
  return extracted.map((f) => ({
    ...f,
    // react-doctor-disable-next-line react-doctor/no-create-object-url-without-revoke -- revoked by useBatchUpload's cleanup on `pending`, which every returned file reaches
    previewUrl: URL.createObjectURL(f.blob),
  }));
}

/** A single `.zip` is unpacked; anything else is read as photos. */
function readSelection(
  files: File[],
  t: TFunction,
  onCount: (n: number) => void,
): Promise<ReadOutcome> {
  const zip = selectedZip(files);
  if (zip) {
    return readZip(zip, t, onCount);
  }
  return readImages(files, onCount).then((extracted) => ({ files: extracted }));
}

function chunk<T>(items: T[], size: number): T[][] {
  const batches: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    batches.push(items.slice(i, i + size));
  }
  return batches;
}

/**
 * State and actions of the batch flow: extract → validate → upload. Called by
 * the page, which reads `flowState` for its stepper and hands the rest to
 * `BatchUpload`.
 */
export function useBatchUpload() {
  const { t } = useTranslation();
  const [phase, setPhase] = useState<Phase>('idle');
  const [pending, setPending] = useState<PendingFile[]>([]);
  const [validation, setValidation] = useState<ValidationResult[]>([]);
  const [extractCount, setExtractCount] = useState(0);
  const [uploadProgress, setUploadProgress] = useState({
    current: 0,
    total: 0,
  });
  const [results, setResults] = useState<BatchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  // Le ZIP contient des salariés : on confirme avant d'envoyer, car leurs
  // photos deviendront des demandes qu'un autre opérateur devra approuver.
  const [confirmEmployeesOpen, setConfirmEmployeesOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    return () => {
      pending.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    };
  }, [pending]);

  async function handleFileChange(e: ChangeEvent<HTMLInputElement>) {
    const files = Array.from(e.target.files ?? []);
    if (files.length === 0) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    setError(null);
    setResults([]);
    setValidation([]);
    pending.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setPending([]);
    setExtractCount(0);

    // --- Extract ---
    setPhase('extracting');
    // Checked before the first await, so a refused selection never renders
    // the extracting phase.
    const refusal = selectedZip(files) ? null : refusedTypes(files, t);
    if (refusal) {
      setError(refusal);
      setPhase('idle');
      return;
    }
    let outcome: ReadOutcome;
    try {
      outcome = await readSelection(files, t, setExtractCount);
    } catch (err) {
      outcome = {
        error: err instanceof Error ? err.message : 'Failed to read files',
      };
    }
    if ('error' in outcome) {
      setError(outcome.error);
      setPhase('idle');
      return;
    }
    const extracted = outcome.files;

    setPending(extracted);

    // --- Validate ---
    setPhase('validating');
    try {
      const data = await validateBatch(extracted.map((f) => f.upn));
      setValidation(data);
      setPhase('ready');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
      setPhase('idle');
    }
  }

  async function handleConfirmUpload() {
    const found = pending.filter(
      (f) => validation.find((v) => v.upn === f.upn)?.found,
    );
    if (found.length === 0) {
      return;
    }

    setConfirmEmployeesOpen(false);
    found.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setPhase('uploading');
    setError(null);

    const batches = chunk(found, UPLOAD_BATCH_SIZE);
    setUploadProgress({ current: 0, total: batches.length });

    const allResults: BatchResult[] = [];
    try {
      // One batch after the other: Array.fromAsync awaits each upload before
      // starting the next. The progress bar counts the batch in flight, each
      // batch is a 50-photo multipart the server processes whole, and the
      // first failure (it throws) stops the batches after it.
      const perBatch = await Array.fromAsync(
        batches.entries(),
        ([i, batch]) => {
          setUploadProgress({ current: i + 1, total: batches.length });
          // File, not Blob: the basename is what the server matches the UPN
          // on, and only a File carries a name through FormData serialization.
          return uploadBatchPhotos(
            batch.map(
              (f) => new File([f.blob], f.filename, { type: f.blob.type }),
            ),
          );
        },
      );
      allResults.push(...perBatch.flat());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed');
      setPhase('ready');
      return;
    }

    setPending([]);
    setResults(allResults);
    setPhase('done');
  }

  function handleReset() {
    pending.forEach((f) => URL.revokeObjectURL(f.previewUrl));
    setPending([]);
    setValidation([]);
    setResults([]);
    setError(null);
    setExtractCount(0);
    setConfirmEmployeesOpen(false);
    setPhase('idle');
  }

  const flowState: BatchUploadFlowState = {
    phase,
    pendingCount: pending.length,
    resultsCount: results.length,
    hasError: !!error,
  };

  return {
    flowState,
    phase,
    pending,
    validation,
    extractCount,
    uploadProgress,
    results,
    error,
    confirmEmployeesOpen,
    setConfirmEmployeesOpen,
    fileInputRef,
    handleFileChange,
    handleConfirmUpload,
    handleReset,
  };
}
